import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import { UserPlus, ArrowLeft, QrCode, Copy, RefreshCcw } from 'lucide-react';
import Layout from '@/components/Layout';

export default function AddTenantPage() {
  const PHONE_REGEX = /^\d{10}$/;
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const AADHAAR_REGEX = /^\d{12}$/;
  const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

  const [pgs, setPgs] = useState([]);
  const [vacantBeds, setVacantBeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [formData, setFormData] = useState({
    pgId: '',
    name: '',
    phone: '',
    email: '',
    idProofType: 'Aadhaar',
    idProofNumber: '',
    emergencyContact: '',
    bedId: '',
    joinDate: new Date().toISOString().split('T')[0],
    monthlyRent: 5000,
    depositAmount: 10000
  });
  const navigate = useNavigate();

  const toNumberOrEmpty = (value) => {
    if (value === '' || value === null || value === undefined) {
      return '';
    }

    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? '' : parsedValue;
  };

  useEffect(() => {
    fetchPgs();
  }, []);

  useEffect(() => {
    if (formData.pgId) {
      setInviteData(null);
      fetchVacantBeds();
      const selectedPg = pgs.find((pg) => Number(pg.id) === Number(formData.pgId));
      if (selectedPg) {
        setFormData(prev => ({
          ...prev,
          monthlyRent: selectedPg.defaultRent,
          depositAmount: selectedPg.defaultDeposit
        }));
      }
    }
  }, [formData.pgId, pgs]);

  const fetchPgs = async () => {
    try {
      const response = await api.getPGs();
      setPgs(response.data);
      if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, pgId: response.data[0].id }));
      }
    } catch (error) {
      toast.error('Failed to fetch PGs');
    }
  };

  const fetchVacantBeds = async () => {
    try {
      const response = await api.getVacantBeds(formData.pgId);
      setVacantBeds(response.data);
      if (response.data.length > 0) {
        setFormData(prev => ({
          ...prev,
          bedId: response.data[0].bedId,
          monthlyRent: response.data[0].rent
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          bedId: ''
        }));
      }
    } catch (error) {
      toast.error('Failed to fetch vacant beds');
    }
  };

  const inviteUrl = inviteData ? `${window.location.origin}/tenants/join/${inviteData.inviteId}` : '';

  const handleGenerateInvite = async () => {
    if (!formData.bedId) {
      toast.error('Select a vacant bed before generating a QR');
      return;
    }

    setInviteLoading(true);
    try {
      const response = await api.createTenantQrInvite({ bedId: Number(formData.bedId) });
      setInviteData(response.data);
      toast.success('QR invite generated');
    } catch (error) {
      toast.error(error.message || 'Failed to generate QR invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyInvite = async () => {
    if (!inviteUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success('Invite link copied');
    } catch (error) {
      toast.error('Unable to copy invite link');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedName = formData.name.trim();
    const trimmedPhone = formData.phone.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedEmergencyContact = formData.emergencyContact.trim();
    const trimmedIdProofType = formData.idProofType.trim();
    const trimmedIdProofNumber = formData.idProofNumber.trim();

    if (!PHONE_REGEX.test(trimmedPhone)) {
      toast.error('Phone number must be exactly 10 digits');
      return;
    }

    if (!PHONE_REGEX.test(trimmedEmergencyContact)) {
      toast.error('Emergency contact must be exactly 10 digits');
      return;
    }

    if (trimmedEmail && !EMAIL_REGEX.test(trimmedEmail)) {
      toast.error('Enter a valid email address');
      return;
    }

    if (trimmedIdProofType === 'Aadhaar' && !AADHAAR_REGEX.test(trimmedIdProofNumber)) {
      toast.error('Aadhaar number must be exactly 12 digits');
      return;
    }

    if (trimmedIdProofType === 'PAN Card' && !PAN_REGEX.test(trimmedIdProofNumber.toUpperCase())) {
      toast.error('PAN number must be in valid PAN format');
      return;
    }

    const payload = {
      ...formData,
      pgId: toNumberOrEmpty(formData.pgId),
      bedId: toNumberOrEmpty(formData.bedId),
      name: trimmedName,
      phone: trimmedPhone,
      email: trimmedEmail || null,
      idProofType: trimmedIdProofType,
      idProofNumber: trimmedIdProofType === 'PAN Card'
        ? trimmedIdProofNumber.toUpperCase()
        : trimmedIdProofNumber,
      emergencyContact: trimmedEmergencyContact,
      monthlyRent: Number(formData.monthlyRent),
      depositAmount: Number(formData.depositAmount)
    };

    if (!payload.bedId) {
      toast.error('Please select a vacant bed before adding a tenant');
      return;
    }

    setLoading(true);
    try {
      await api.createTenant(payload);
      toast.success('Tenant added successfully!');
      navigate('/tenants');
    } catch (error) {
      toast.error(error.message || error.data?.message || 'Failed to add tenant');
    } finally {
      setLoading(false);
    }
  };

  if (pgs.length === 0) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <h2 className="text-xl font-bold text-slate-900 mb-2">Setup Required</h2>
            <p className="text-slate-600 mb-4">Please setup your PG first before adding tenants</p>
            <Button onClick={() => navigate('/setup')} data-testid="setup-redirect-button">
              Go to Setup
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <Button
          onClick={() => navigate('/tenants')}
          variant="ghost"
          className="mb-4"
          data-testid="back-to-tenants-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tenants
        </Button>

        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <UserPlus className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-heading">Add New Tenant</h1>
              <p className="text-slate-600 text-sm">Fill in tenant details carefully</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Select PG</Label>
              <select
                value={formData.pgId}
                onChange={(e) => setFormData({ ...formData, pgId: Number(e.target.value), bedId: '' })}
                className="h-12 w-full px-4 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-primary/20"
                required
                data-testid="tenant-pg-select"
              >
                {pgs.map((pg) => (
                  <option key={pg.id} value={pg.id}>{pg.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Full Name *</Label>
                <Input
                  placeholder="Rahul Sharma"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-12 rounded-lg"
                  required
                  data-testid="tenant-name-input"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Phone Number *</Label>
                <Input
                  placeholder="9876543210"
                  value={formData.phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData({ ...formData, phone: value });
                  }}
                  inputMode="numeric"
                  maxLength={10}
                  className="h-12 rounded-lg"
                  required
                  data-testid="tenant-phone-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Email (Optional)</Label>
              <Input
                type="email"
                placeholder="rahul@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-12 rounded-lg"
                data-testid="tenant-email-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">ID Proof Type *</Label>
                <select
                  value={formData.idProofType}
                  onChange={(e) => setFormData({ ...formData, idProofType: e.target.value, idProofNumber: '' })}
                  className="h-12 w-full px-4 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-primary/20"
                  data-testid="tenant-id-type-select"
                >
                  <option>Aadhaar</option>
                  <option>PAN Card</option>
                  <option>Driving License</option>
                  <option>Passport</option>
                </select>
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">ID Proof Number *</Label>
                <Input
                  placeholder={formData.idProofType === 'Aadhaar' ? '123412341234' : formData.idProofType === 'PAN Card' ? 'ABCDE1234F' : 'Enter ID number'}
                  value={formData.idProofNumber}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (formData.idProofType === 'Aadhaar') {
                      value = value.replace(/\D/g, '').slice(0, 12);
                    } else if (formData.idProofType === 'PAN Card') {
                      value = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
                    }

                    setFormData({ ...formData, idProofNumber: value });
                  }}
                  inputMode={formData.idProofType === 'Aadhaar' ? 'numeric' : undefined}
                  maxLength={formData.idProofType === 'Aadhaar' ? 12 : formData.idProofType === 'PAN Card' ? 10 : undefined}
                  className="h-12 rounded-lg"
                  required
                  data-testid="tenant-id-number-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Emergency Contact *</Label>
              <Input
                placeholder="9123456789"
                value={formData.emergencyContact}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setFormData({ ...formData, emergencyContact: value });
                }}
                inputMode="numeric"
                maxLength={10}
                className="h-12 rounded-lg"
                required
                data-testid="tenant-emergency-input"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Select Bed *</Label>
              <select
                value={formData.bedId}
                onChange={(e) => {
                  const selectedBed = vacantBeds.find((b) => Number(b.bedId) === Number(e.target.value));
                  setFormData({
                    ...formData,
                    bedId: Number(e.target.value),
                    monthlyRent: selectedBed?.rent || formData.monthlyRent
                  });
                  setInviteData(null);
                }}
                className="h-12 w-full px-4 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-primary/20"
                required
                data-testid="tenant-bed-select"
              >
                {vacantBeds.length === 0 ? (
                  <option>No vacant beds available</option>
                ) : (
                  vacantBeds.map((bed) => (
                    <option key={bed.bedId} value={bed.bedId}>
                      {bed.floorName} - Room {bed.roomNumber} - Bed {bed.bedNumber} (₹{bed.rent})
                    </option>
                  ))
                )}
              </select>
              {vacantBeds.length === 0 && (
                <p className="text-sm text-red-600 mt-1">Please add rooms and beds first in the Setup page</p>
              )}
            </div>

            <div className="rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-4 sm:p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-slate-900">
                    <QrCode className="h-5 w-5 text-primary" />
                    <h2 className="text-lg font-bold font-heading">Invite Via QR</h2>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">
                    Keep the normal Add Tenant flow as-is, or generate a one-time QR for the selected bed so the tenant can fill in the same details from their phone.
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    The QR expires after 30 minutes or right after the first successful submission.
                  </p>
                </div>

                <Button
                  type="button"
                  onClick={handleGenerateInvite}
                  className="h-11 rounded-xl bg-slate-900 px-5 text-white hover:bg-slate-800 sm:w-auto"
                  disabled={inviteLoading || vacantBeds.length === 0 || !formData.bedId}
                >
                  <QrCode className="mr-2 h-4 w-4" />
                  {inviteLoading ? 'Generating...' : inviteData ? 'Refresh QR' : 'Show QR'}
                </Button>
              </div>

              {inviteData && (
                <div className="mt-5 grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
                  <div className="rounded-2xl bg-white p-4 shadow-sm">
                    <div className="rounded-xl bg-white p-3">
                      <QRCode value={inviteUrl} size={180} className="h-auto w-full" />
                    </div>
                  </div>

                  <div className="space-y-4 rounded-2xl bg-white p-4 shadow-sm">
                    <div>
                      <p className="text-sm font-semibold text-slate-700">Invite for</p>
                      <p className="mt-1 font-bold text-slate-900">
                        {inviteData.pgName} - {inviteData.floorName} - Room {inviteData.roomNumber} - Bed {inviteData.bedNumber}
                      </p>
                      <p className="mt-1 text-sm text-slate-500">
                        Expires at {new Date(inviteData.expiresAt).toLocaleString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-xl bg-slate-50 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Suggested Rent</p>
                        <p className="mt-1 text-lg font-bold text-slate-900">
                          ₹{Number(inviteData.suggestedRent || 0).toLocaleString('en-IN')}
                        </p>
                      </div>
                      <div className="rounded-xl bg-slate-50 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Suggested Deposit</p>
                        <p className="mt-1 text-lg font-bold text-slate-900">
                          ₹{Number(inviteData.suggestedDeposit || 0).toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>

                    <div>
                      <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Invite Link</Label>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Input value={inviteUrl} readOnly className="h-11 rounded-lg text-xs sm:text-sm" />
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 rounded-lg"
                            onClick={handleCopyInvite}
                          >
                            <Copy className="mr-2 h-4 w-4" />
                            Copy
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 rounded-lg"
                            onClick={handleGenerateInvite}
                            disabled={inviteLoading}
                          >
                            <RefreshCcw className="mr-2 h-4 w-4" />
                            New QR
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Join Date *</Label>
                <Input
                  type="date"
                  value={formData.joinDate}
                  onChange={(e) => setFormData({ ...formData, joinDate: e.target.value })}
                  className="h-12 rounded-lg"
                  required
                  data-testid="tenant-join-date-input"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Monthly Rent (₹) *</Label>
                <Input
                  type="number"
                  placeholder="5000"
                  value={formData.monthlyRent}
                  onChange={(e) => setFormData({ ...formData, monthlyRent: parseFloat(e.target.value) })}
                  className="h-12 rounded-lg"
                  required
                  data-testid="tenant-rent-input"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Deposit (₹) *</Label>
                <Input
                  type="number"
                  placeholder="10000"
                  value={formData.depositAmount}
                  onChange={(e) => setFormData({ ...formData, depositAmount: parseFloat(e.target.value) })}
                  className="h-12 rounded-lg"
                  required
                  data-testid="tenant-deposit-input"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={() => navigate('/tenants')}
                variant="outline"
                className="flex-1 h-12 rounded-xl"
                data-testid="cancel-tenant-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-12 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold"
                disabled={loading || vacantBeds.length === 0}
                data-testid="submit-tenant-button"
              >
                {loading ? 'Adding...' : 'Add Tenant'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
